import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, IconButton, InputAdornment, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Add, ContentCopy, Inbox, Language, OpenInNew, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Breadcrumb } from '../../components/layout/Breadcrumb';
import { ROUTES } from '../../constants/routes';
import { listIntakes } from '../../services/intakeService';
import type { IntakeRequestRow, IntakeStatus } from '../../types/intake';
import { CreateIntakeLinkDialog } from './CreateIntakeLinkDialog';
import { QuotesSideRail } from '../quotes/QuotesSideRail';
import { usePermissions } from '../../hooks/usePermissions';

export const INTAKE_STATUS_COLORS: Record<IntakeStatus, string> = {
  Draft: '#757575',
  Submitted: '#e65100',
  Returned: '#c62828',
  ConvertedQuote: '#2e7d32',
  ConvertedWorkOrder: '#1565c0',
  Cancelled: '#9e9e9e',
};

export const INTAKE_STATUS_LABELS: Record<IntakeStatus, string> = {
  Draft: 'Draft (with requester)',
  Submitted: 'Submitted',
  Returned: 'Returned to requester',
  ConvertedQuote: 'Converted → Quote',
  ConvertedWorkOrder: 'Converted → Work Order',
  Cancelled: 'Cancelled',
};

type FilterKey = 'all' | 'needs-review' | 'open' | 'converted';

const FILTERS: { key: FilterKey; label: string; color: string; description: string }[] = [
  { key: 'all', label: 'All', color: '#4A6741', description: 'Every intake request' },
  { key: 'needs-review', label: 'Needs review', color: '#e65100', description: 'Submitted — waiting on the lab' },
  { key: 'open', label: 'Open', color: '#1565c0', description: 'Draft, submitted or returned' },
  { key: 'converted', label: 'Converted', color: '#2e7d32', description: 'Became a quote or work order' },
];

const HEADERS = ['Status', 'Project', 'Client', 'Contact', 'Samples / Tests', 'Submitted', 'Link expires', ''];

/**
 * Lab-side inbox of client intake requests, living inside the shared Quotes
 * layout (same side rail as the quotes list).
 */
export const IntakeRequestsPage = () => {
  const { isAdmin } = usePermissions();
  const theme = useTheme();
  const navigate = useNavigate();
  const [rows, setRows] = useState<IntakeRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  // Default = everything: a freshly created link (still Draft) shows up
  // immediately instead of hiding behind a needs-review filter.
  const [filter, setFilter] = useState<FilterKey>('all');
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [portalCopied, setPortalCopied] = useState(false);
  const [railRefresh, setRailRefresh] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await listIntakes());
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    let list = rows;
    switch (filter) {
      case 'needs-review': list = list.filter((r) => r.Status === 'Submitted'); break;
      case 'open': list = list.filter((r) => ['Draft', 'Submitted', 'Returned'].includes(r.Status)); break;
      case 'converted': list = list.filter((r) => r.Status.startsWith('Converted')); break;
    }
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((r) =>
        (r.ProjectName ?? '').toLowerCase().includes(s) ||
        (r.ClientName ?? '').toLowerCase().includes(s) ||
        (r.ContactName ?? '').toLowerCase().includes(s) ||
        (r.ContactEmail ?? '').toLowerCase().includes(s),
      );
    }
    return list;
  }, [rows, filter, search]);

  const counts = useMemo(() => ({
    'needs-review': rows.filter((r) => r.Status === 'Submitted').length,
    open: rows.filter((r) => ['Draft', 'Submitted', 'Returned'].includes(r.Status)).length,
    converted: rows.filter((r) => r.Status.startsWith('Converted')).length,
    all: rows.length,
  }), [rows]);

  const copyLink = async (row: IntakeRequestRow, e: React.MouseEvent) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(`${window.location.origin}/intake/${row.Token}`);
    setCopiedKey(row.IntakeRequestKey);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      <QuotesSideRail active="intake" intakeCountKey={railRefresh} />

      <Box sx={{ flex: 1, p: 2.5, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Breadcrumb items={[{ label: 'Quotes', path: ROUTES.QUOTES }, { label: 'Intake Requests' }]} />
          <Box sx={{ flex: 1 }} />
          <TextField size="small" placeholder="Search intakes..." value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18, color: 'text.secondary' }} /></InputAdornment> }}
            sx={{ width: 240, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.8rem' } }} />
          <Tooltip title={portalCopied
            ? 'Copied!'
            : 'The standing portal URL — hand it out like the old Excel file. Anyone with it can start a new request any time.'}>
            <Button variant="outlined" size="small" startIcon={<Language />}
              onClick={async () => {
                await navigator.clipboard.writeText(`${window.location.origin}${ROUTES.PUBLIC_INTAKE_START}`);
                setPortalCopied(true);
                setTimeout(() => setPortalCopied(false), 1500);
              }}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
              Copy Portal Link
            </Button>
          </Tooltip>
          {/* BUG-2026-0182: open the standing portal in a new tab — the lab
              starts new requests here too, not only clients. */}
          <Tooltip title="Open the intake request portal in a new tab — start a new request the way a client would.">
            <Button variant="outlined" size="small" startIcon={<OpenInNew />}
              onClick={() => window.open(ROUTES.PUBLIC_INTAKE_START, '_blank', 'noopener,noreferrer')}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
              Open Portal
            </Button>
          </Tooltip>
          {isAdmin && (
            <Button variant="contained" size="small" startIcon={<Add />}
              onClick={() => setDialogOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
              New Intake Link
            </Button>
          )}
        </Box>

        {/* Segmented filter — always shows every bucket's count, and says
            plainly when the list is a filtered slice. */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', border: 1, borderColor: 'divider', borderRadius: '10px', overflow: 'hidden' }}>
            {FILTERS.map((f, i) => {
              const active = filter === f.key;
              return (
                <Tooltip key={f.key} title={f.description}>
                  <Box
                    onClick={() => setFilter(f.key)}
                    sx={{
                      px: 1.75, py: 0.75, cursor: 'pointer', userSelect: 'none',
                      display: 'flex', alignItems: 'center', gap: 0.75,
                      borderLeft: i > 0 ? 1 : 0, borderColor: 'divider',
                      bgcolor: active ? alpha(f.color, 0.12) : 'transparent',
                      boxShadow: active ? `inset 0 -2px 0 ${f.color}` : 'none',
                      '&:hover': { bgcolor: alpha(f.color, active ? 0.16 : 0.06) },
                      transition: 'background-color 0.15s ease',
                    }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: f.color, opacity: active ? 1 : 0.45 }} />
                    <Typography variant="body2" sx={{ fontWeight: active ? 700 : 500, fontSize: '0.8rem' }}>
                      {f.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                      {counts[f.key]}
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {filter === 'all' && !search
              ? `Showing all ${rows.length} request${rows.length === 1 ? '' : 's'}`
              : `Showing ${filtered.length} of ${rows.length} request${rows.length === 1 ? '' : 's'}${search ? ' (search applied)' : ''}`}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 6, color: 'text.secondary' }}>
            <Inbox sx={{ fontSize: 42, opacity: 0.4 }} />
            <Typography variant="body2">
              {rows.length === 0
                ? 'No intake requests yet — create a link, or share the standing portal link with the project teams.'
                : filter === 'needs-review'
                  ? 'No submitted intakes waiting for review.'
                  : 'Nothing matches this filter.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: '10px', bgcolor: 'background.paper' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {HEADERS.map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'text.secondary', py: 1.25 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r) => {
                  const color = INTAKE_STATUS_COLORS[r.Status] ?? theme.palette.text.primary;
                  return (
                    <TableRow
                      key={r.IntakeRequestKey}
                      hover
                      onClick={() => navigate(ROUTES.INTAKE_DETAIL.replace(':intakeKey', r.IntakeRequestKey))}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>
                        <Chip
                          label={INTAKE_STATUS_LABELS[r.Status] ?? r.Status}
                          size="small"
                          sx={{ fontWeight: 600, fontSize: '0.7rem', color, bgcolor: alpha(color, 0.12) }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{r.ProjectName ?? '—'}</TableCell>
                      <TableCell>{r.ClientName ?? '—'}</TableCell>
                      <TableCell>
                        {r.ContactName
                          ? `${r.ContactName}${r.ContactEmail ? ` (${r.ContactEmail})` : ''}`
                          : (r.ContactEmail ?? '—')}
                      </TableCell>
                      <TableCell>{r.SampleCount} / {r.TestCount}</TableCell>
                      <TableCell>{r.SubmittedAt ? String(r.SubmittedAt).slice(0, 10) : '—'}</TableCell>
                      <TableCell>{r.ExpiresAt ? String(r.ExpiresAt).slice(0, 10) : '—'}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        <Tooltip title={copiedKey === r.IntakeRequestKey ? 'Copied!' : 'Copy form link'}>
                          <IconButton size="small" onClick={(e) => copyLink(r, e)}>
                            <ContentCopy sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <CreateIntakeLinkDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onCreated={() => { fetchData(); setRailRefresh((k) => k + 1); }}
        />
      </Box>
    </Box>
  );
};

export default IntakeRequestsPage;
